/**
 * Cuántas veces Gustavo ya ofreció una novedad, y cuáles no tiene que volver a ofrecer.
 *
 * El backend dice CUÁL es la novedad pendiente; esto decide si corresponde mostrarla
 * ahora. Son dos cosas distintas y por eso viven separadas: lo primero es un hecho del
 * servidor, lo segundo es cortesía — cuántas veces se puede insistir sin volverse ruido.
 *
 * Reglas:
 *  · Tope de 3 apariciones por novedad y por DÍA. Al cuarto intento del día se calla.
 *  · Si la abrió o la cerró con la X, no vuelve más (`listo`). El backend igual va a
 *    dejar de mandarla en cuanto entre el page_view, pero eso tarda hasta 60 s por el
 *    cache: sin la marca local, la burbuja volvía a aparecer después de haberla abierto.
 *
 * Vive en localStorage y no en la base a propósito: es preferencia de presentación, no
 * un dato de negocio. La contra conocida es que es POR NAVEGADOR — quien entre desde dos
 * dispositivos puede ver la misma novedad ofrecida en los dos. Se asume: llevarlo al
 * servidor costaría un modelo y una escritura por aparición para ahorrar una repetición.
 * La LECTURA, que es lo que se mide, sí sale del servidor (analytics_events).
 */

/**
 * La versión va en la clave porque lo guardado bajo `v1` se contó con otra regla: ahí una
 * aparición se anotaba al mostrarse, así que recargar la pantalla gastaba una de las tres
 * sin que nadie leyera nada. Con dos recargas alcanzaba para quedarse sin novedad hasta el
 * día siguiente. Ahora se cuenta recién a los segundos de estar en pantalla, y esos números
 * viejos dirían "ya la viste tres veces" sobre apariciones que nunca ocurrieron.
 * Si vuelve a cambiar cómo se cuenta, subir la versión otra vez.
 */
const PREFIJO = 'gustavo:novedad:v2';
export const TOPE_POR_DIA = 3;

/**
 * En `npm run dev` el tope diario no se aplica.
 *
 * No es una comodidad: con 3 por día, mirar el cambio dos veces te deja sin poder verlo
 * hasta el día siguiente, y lo que se ve entonces —una esquina vacía— es indistinguible
 * de que esté roto. Se perdieron varias vueltas persiguiendo eso.
 *
 * Lo que SÍ se sigue respetando en local es `listo`: si la abrió o la descartó, no vuelve.
 * Eso es comportamiento, no dosificación, y hay que poder probarlo.
 *
 * `MODE` y no `DEV` a propósito: en los tests `MODE` es 'test' y el tope tiene que valer,
 * que es justamente lo que ese test verifica.
 */
const SIN_TOPE_LOCAL = import.meta.env.MODE === 'development';

interface Estado {
  /** Día de las apariciones contadas, YYYY-MM-DD. Otro día → el contador arranca de cero. */
  dia:   string;
  veces: number;
  /** La abrió o la descartó: no se ofrece nunca más. */
  listo?: boolean;
}

/** La clave lleva el usuario: en una computadora compartida cada uno tiene lo suyo. */
const clave = (userId: string) => `${PREFIJO}:${userId}`;

const hoy = () => new Date().toISOString().slice(0, 10);

function leer(userId: string): Record<string, Estado> {
  try {
    const raw = localStorage.getItem(clave(userId));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function guardar(userId: string, mapa: Record<string, Estado>) {
  try { localStorage.setItem(clave(userId), JSON.stringify(mapa)); } catch { /* modo privado */ }
}

/** ¿Se puede mostrar ahora? No consume nada — sirve para decidir si arrancar la rotación. */
export function puedeOfrecerse(userId: string, postId: string): boolean {
  const e = leer(userId)[postId];
  if (e?.listo) return false;          // la abrió o la descartó: se terminó
  if (!e || SIN_TOPE_LOCAL) return true;
  return e.dia !== hoy() || e.veces < TOPE_POR_DIA;
}

/**
 * Anota una aparición. Devuelve `false` si ya no correspondía mostrarla — quien llama
 * usa eso para cortar la rotación en vez de seguir agendando timers.
 */
export function anotarAparicion(userId: string, postId: string): boolean {
  const mapa = leer(userId);
  const e = mapa[postId];
  if (e?.listo) return false;

  const dia = hoy();
  const veces = e && e.dia === dia ? e.veces : 0;
  if (veces >= TOPE_POR_DIA && !SIN_TOPE_LOCAL) return false;

  mapa[postId] = { dia, veces: veces + 1 };
  guardar(userId, mapa);
  return true;
}

/** La abrió o la cerró con la X: se termina para esa novedad. */
export function darPorVista(userId: string, postId: string) {
  const mapa = leer(userId);
  mapa[postId] = { dia: hoy(), veces: mapa[postId]?.veces ?? 0, listo: true };
  guardar(userId, mapa);
}

/** Solo para tests. */
export function _limpiarNovedadEnBurbuja(userId: string) {
  try { localStorage.removeItem(clave(userId)); } catch { /* noop */ }
}

// Atajo para probar en local: con 3 apariciones por día, validar el comportamiento se
// vuelve imposible después del tercer refresh. `gustavoResetNovedades()` en la consola
// devuelve el contador a cero. Solo en desarrollo — en producción no existe.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).gustavoResetNovedades = () => {
    for (const k of Object.keys(localStorage)) if (k.startsWith(PREFIJO)) localStorage.removeItem(k);
    console.info('[gustavo] contador de novedades reiniciado — recargá la página');
  };
}
